import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { GavelOutlined, SupportAgentOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMySanctions } from '../../hooks/useSanction';
import { formatDateKST } from '../../utils/dateUtils';

const SanctionBlockPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { sanctions, loading } = useMySanctions();

  const isBanned = user?.status === 'BANNED';
  const targetType = isBanned ? 'BAN' : 'SUSPENSION';
  const activeSanction = sanctions.find((s) => s.sanctionType === targetType);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FFF5F5' }}>
      <Container maxWidth="sm" sx={{ pt: 8, pb: 6 }}>
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 2px 16px rgba(200,0,0,0.10)' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <GavelOutlined sx={{ fontSize: 72, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={800} color="error.main" gutterBottom>
              {isBanned
                ? '서비스 이용이 영구 정지되었습니다'
                : '서비스 이용이 일시 정지되었습니다'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isBanned
                ? '커뮤니티 정책 위반으로 계정이 영구 정지되었습니다.'
                : '커뮤니티 정책 위반으로 계정이 일시 정지되었습니다.'}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} color="error" />
            </Box>
          ) : activeSanction ? (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={activeSanction.sanctionTypeTitle}
                  size="small"
                  color="error"
                  sx={{ fontWeight: 700 }}
                />
                <Typography variant="body2" fontWeight={600}>
                  {activeSanction.categoryTitle}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                처리일: {formatDateKST(activeSanction.createdAt)}
              </Typography>
              {!isBanned && activeSanction.expiresAt && (
                <Typography variant="body2" color="text.secondary">
                  정지 해제일: {formatDateKST(activeSanction.expiresAt)}
                </Typography>
              )}
            </Alert>
          ) : null}

          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            제재 내용에 이의가 있으시면 이의제기를 통해 재검토를 요청하실 수 있습니다.
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {activeSanction && !activeSanction.hasAppealed && (
              <Button
                variant="contained"
                color="error"
                fullWidth
                size="large"
                onClick={() =>
                  navigate(
                    `/appeal?sanctionId=${activeSanction.reportId}&sanctionType=${activeSanction.sanctionType}`
                  )
                }
                sx={{ py: 1.5, fontWeight: 700, borderRadius: 2 }}
              >
                이의제기하기
              </Button>
            )}
            {activeSanction && activeSanction.hasAppealed && (
              <Button
                variant="outlined"
                color="error"
                fullWidth
                size="large"
                onClick={() => navigate('/appeal/list')}
                sx={{ py: 1.5, fontWeight: 700, borderRadius: 2 }}
              >
                이의제기 내역 보기
              </Button>
            )}
            <Button
              variant="outlined"
              fullWidth
              startIcon={<SupportAgentOutlined />}
              onClick={() => navigate('/support')}
              sx={{ py: 1.5, borderRadius: 2 }}
            >
              고객센터 문의
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={handleLogout}
              sx={{ color: 'text.disabled', fontSize: '0.875rem' }}
            >
              로그아웃
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SanctionBlockPage;
